/**
 * Virtual Kiosk (Self Check-in) configuration.
 *
 * The facility-facing values are managed on the Admin Portal
 * (Dashboard > Guest Hub) and stored as the `guest_hub_setting`
 * facility setting. `loadKioskConfig()` fetches that record and merges it
 * over the defaults below, so a facility that has never saved the setting
 * keeps the previous kiosk behavior.
 */

import { lionheart } from '@/modules/checkin-lionheart/module/client';

// Name of the facility setting saved by the Guest Hub admin page.
export const GUEST_HUB_SETTING_NAME = 'guest_hub_setting';

// Facility setting holding the IANA zone the operating hours are written in.
export const TIMEZONE_SETTING_NAME = 'timezone';

// Matches the fallback the check-out timing validation already uses.
const DEFAULT_TIMEZONE = 'Asia/Tokyo';

// Last known good settings, kept only as an offline fallback (see
// loadKioskConfig). Never served in place of a fetch that can succeed.
const CACHE_KEY = 'kiosk_config_cache';

// True when a page was reached through the kiosk flow. Kept as a plain
// function so components can branch on kiosk mode without pulling in the
// validation mixin's timers and session state.
export function isKioskRoute(route) {
    return route?.query?.kiosk === 'true' || route?.meta?.kiosk === true;
}

// The two sides of the Guest Hub. A facility hands out ONE kiosk URL (and QR
// code), /kiosk, and the device is not locked to a side: the side it shows is
// decided by the clock, from the operating hours saved in the Guest Hub setting:
//
//   inside the check-in window   -> the check-in start buttons
//   inside the check-out window  -> the Start Check-out button
//   configured, but between windows -> "Out of Operating Time" (lists both)
//   no side configured           -> "No Operation"
//
// The dashboard refuses overlapping windows, so at most one side is open at a
// time; adjacent ones (check-out 07:00-11:00, check-in 11:00-15:00) are fine
// because the end is exclusive and the start inclusive. Codes printed when the
// URL carried `?mode=checkin|checkout` keep working: the param is ignored.
export const KIOSK_MODES = {
    CHECKIN: 'checkin',
    CHECKOUT: 'checkout',
};

// Whether "now" falls inside a { start: 'HH:mm', end: 'HH:mm' } window. Start
// is inclusive, end exclusive, and a window whose end is before its start wraps
// past midnight (22:00 - 02:00). An unset start or end is never open. `now` is
// a moment already in the facility timezone. Shared by the validation mixin and
// kioskActiveSide so the two can never disagree about when a window is open.
export function isWithinWindow(window, now) {
    if (!window?.start || !window?.end) return false;

    const minutesNow = now.hours() * 60 + now.minutes();
    const [sh, sm] = window.start.split(':').map(Number);
    const [eh, em] = window.end.split(':').map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;

    if (start <= end) return minutesNow >= start && minutesNow < end;
    return minutesNow >= start || minutesNow < end; // overnight window
}

// A side is configured only when the Guest Hub setting switches it on AND gives
// it both ends of an operating window. A side switched on without hours cannot
// be told open from closed, so it counts as not configured (fail closed).
export function kioskSideConfigured(config, side) {
    if (!config) return false;

    const inUse = side === KIOSK_MODES.CHECKOUT
        ? config.checkout_mode !== KIOSK_CHECKOUT_MODES.NOT_IN_USE
        : config.checkin_mode !== KIOSK_CHECKIN_MODES.NOT_IN_USE;
    const hours = config.operating_hours?.[side];

    return inUse && !!hours?.start && !!hours?.end;
}

// The side whose window is open right now: KIOSK_MODES.CHECKIN,
// KIOSK_MODES.CHECKOUT, or null when none is (nothing configured, or between
// windows). Check-in is tried first only as a tie-break the dashboard's
// no-overlap rule makes unreachable.
export function kioskActiveSide(config, now) {
    return [KIOSK_MODES.CHECKIN, KIOSK_MODES.CHECKOUT].find(
        (side) => kioskSideConfigured(config, side)
            && isWithinWindow(config.operating_hours[side], now)
    ) ?? null;
}

// 3-1. Check-in Modes (LP options on the Guest Hub setting page)
export const KIOSK_CHECKIN_MODES = {
    // Guest Hub check-in is not used at all.
    NOT_IN_USE: 0,
    // Pre Check-in guests + guests starting check-in (both accepted)
    BOTH: 1,
    // Guests starting check-in only (hotels that do not require Pre Check-in)
    START_CHECKIN_ONLY: 2,
    // Pre Check-in guests only (pre-registered QR code)
    PRE_CHECKIN_ONLY: 3,
    // Guests filling the Pre Check-in form on the kiosk itself.
    START_PRE_CHECKIN_ONLY: 4,
};

// 3-2. Check-in Flows.
//
// A "flow" is what one landing-page button starts. The three of them differ in
// what the guest already did before walking up to the kiosk, and therefore in
// what status they leave in:
//
//   confirm_pre_checkin  the guest pre-checked in at home and is only
//                        confirming it here    -> booking becomes CHECKED IN
//   start_checkin        the guest never pre-checked in and does the whole
//                        check-in here         -> booking becomes CHECKED IN
//   start_pre_checkin    the guest fills the Pre Check-in form on the kiosk,
//                        the front desk finishes later
//                                              -> booking becomes PRE CHECK-IN
//
// Everything downstream — which methods the hub offers, which reservation
// statuses are accepted, and which endpoint completes the session — is read
// off the flow, so the landing button and the screens behind it can never
// disagree about what is being started.
export const KIOSK_FLOWS = {
    CONFIRM_PRE_CHECKIN: 'confirm_pre_checkin',
    START_CHECKIN: 'start_checkin',
    START_PRE_CHECKIN: 'start_pre_checkin',
};

// Which flows each landing mode puts on the home screen, in display order.
// KIOSK_CHECKIN_MODES.BOTH is the only mode with two buttons; the rest land
// the guest in a single flow (see kioskAutoFlow below).
export const KIOSK_MODE_FLOWS = {
    [KIOSK_CHECKIN_MODES.NOT_IN_USE]: [],
    [KIOSK_CHECKIN_MODES.BOTH]: [KIOSK_FLOWS.START_CHECKIN, KIOSK_FLOWS.CONFIRM_PRE_CHECKIN],
    [KIOSK_CHECKIN_MODES.START_CHECKIN_ONLY]: [KIOSK_FLOWS.START_CHECKIN],
    [KIOSK_CHECKIN_MODES.PRE_CHECKIN_ONLY]: [KIOSK_FLOWS.CONFIRM_PRE_CHECKIN],
    [KIOSK_CHECKIN_MODES.START_PRE_CHECKIN_ONLY]: [KIOSK_FLOWS.START_PRE_CHECKIN],
};

// The reservation-search methods each flow offers, in display order.
//
//   qr              pre-registered QR code (email|pass, or the reservation
//                   number when "check-in by reservation number" is on) — the
//                   code from the guest's mail. Offered by Start Check-in too:
//                   a guest holding one should not have to type a name when
//                   they walked up to the wrong button.
//   name/phone      /facility-booking/list, scoped to today's check-in date.
//   email           /facility-booking/list by the reservation's email.
//   reservation_no  /facility-booking/list by PMS reservation number.
//   pass            the pass from the guest's pre-check-in mail, on its own
//                   (mode "pass-only", which scopes it to the stay that covers
//                   today — a pass from a past visit finds nothing).
//   others          no reservation to find — a walk-in fills the form from
//                   scratch. Confirm Pre Check-in does not offer it: that flow
//                   exists to finish a booking that already went through Pre
//                   Check-in, and a walk-in has none.
export const KIOSK_FLOW_METHODS = {
   //  [KIOSK_FLOWS.CONFIRM_PRE_CHECKIN]: ['qr', 'name', 'phone', 'email', 'reservation_no', 'pass'],
    [KIOSK_FLOWS.CONFIRM_PRE_CHECKIN]: ['qr', 'name', 'phone', 'email', 'reservation_no'],
    [KIOSK_FLOWS.START_CHECKIN]: ['qr', 'name', 'phone', 'email', 'reservation_no', 'others'],
   //  [KIOSK_FLOWS.START_PRE_CHECKIN]: ['name', 'phone', 'email', 'reservation_no', 'pass'],
    [KIOSK_FLOWS.START_PRE_CHECKIN]: ['name', 'phone', 'email', 'reservation_no'],
};

// The `search_methods` keys the Guest Hub setting can switch off. `pass` and
// `others` have no toggle — they are part of the flow that lists them.
const CONFIGURABLE_METHODS = ['qr', 'name', 'phone', 'email', 'reservation_no'];

// The methods a flow actually shows: its own list, minus anything the facility
// switched off in Reservation Search.
export function kioskFlowMethods(flow, searchMethods) {
    const methods = KIOSK_FLOW_METHODS[flow] ?? [];
    const enabled = searchMethods ?? defaultKioskConfig.search_methods;

    return methods.filter(
        (method) => !CONFIGURABLE_METHODS.includes(method) || enabled[method] !== false
    );
}

// The flows a saved check-in mode offers.
export function kioskFlowsForMode(mode) {
    return KIOSK_MODE_FLOWS[mode] ?? [];
}

// The flow a mode with a single button starts. Used to recover the flow when
// the hub is opened without one (a bookmarked URL, a staff deep link): with
// one button there is no ambiguity, and with two there is, so the guest is
// sent back to the home screen to press one.
export function kioskAutoFlow(mode) {
    const flows = kioskFlowsForMode(mode);
    return flows.length === 1 ? flows[0] : '';
}

// Check-out Modes (single scenario besides off — must switch Quick CO ON)
export const KIOSK_CHECKOUT_MODES = {
    NOT_IN_USE: 0,
    START_CHECKOUT: 1,
};

export const defaultKioskConfig = {
    // Master switch — when false the kiosk shows the "No Operation" screen.
    // Derived from the saved modes; both off (the state of every facility
    // until staff saves the Guest Hub setting) means the hub is not operating.
    enabled: false,

    // Fail closed: a facility that has never saved the Guest Hub setting must
    // show "No Operation", never a check-in button nobody configured.
    checkin_mode: KIOSK_CHECKIN_MODES.NOT_IN_USE,
    checkout_mode: KIOSK_CHECKOUT_MODES.NOT_IN_USE,

    // Operating windows ("HH:mm", 24h). Windows that wrap past midnight
    // (e.g. 22:00 - 02:00) are supported by the validation mixin.
    // Left empty on purpose — hours only ever come from the saved setting.
    operating_hours: {
        checkin: { start: '', end: '' },
        checkout: { start: '', end: '' },
    },

    // The facility's timezone, which is what the operating windows are written
    // in. The kiosk compares them against the clock in THIS zone, never the
    // tablet's own — a device imaged in the wrong region (or with a drifted
    // clock) would otherwise open and close the hub at the wrong hour.
    timezone: DEFAULT_TIMEZONE,

    // Editable text shown on the "Out of Operating Time" screen.
    // Empty string falls back to the translated default message.
    out_of_operation_message: '',

    // 4. Reservation Search — each method individually configurable.
    //
    // On by default: every flow in KIOSK_FLOW_METHODS lists the methods it
    // needs, and this setting only takes them away. The Guest Hub admin page
    // does not save this key yet, so leaving one off here would silently hide
    // a method the flow is specified to offer.
    //
    // `qr` is listed by Confirm Pre Check-in and Start Check-in, so switching
    // it off here takes the camera away from both; Start Pre Check-in never
    // offers it either way.
    search_methods: {
        qr: true,
        name: true,
        phone: true,
        email: true,
        reservation_no: true,
    },

    // 7. Session management
    // Seconds of inactivity before the auto-reset warning appears. Every screen
    // a guest is working on (reservation search, check-in form, confirmation,
    // payment input) warns after 3 minutes of no touch.
    idle_timeout_seconds: 180,
    // Countdown shown on the warning modal before forcing reset.
    idle_warning_seconds: 60,
    // The screens at the end of a flow — completed and error — carry no
    // warning: there is nothing left for the guest to answer, so they simply
    // return to the start screen a minute after they appear.
    idle_auto_return_seconds: 60,
};

// A fresh copy of the closed defaults (nested objects included, so callers
// can never mutate the shared constant).
function closedKioskConfig() {
    return {
        ...defaultKioskConfig,
        operating_hours: {
            checkin: { ...defaultKioskConfig.operating_hours.checkin },
            checkout: { ...defaultKioskConfig.operating_hours.checkout },
        },
        search_methods: { ...defaultKioskConfig.search_methods },
    };
}

/**
 * Merge a saved `guest_hub_setting` value (and the facility timezone) over the
 * closed defaults. Every open/close decision the kiosk makes runs against the
 * object this returns.
 */
export function buildKioskConfig(value, timezone) {
    const config = closedKioskConfig();
    const saved = value ?? {};

    config.checkin_mode = saved.checkin_mode ?? config.checkin_mode;
    config.checkout_mode = saved.checkout_mode ?? config.checkout_mode;
    config.operating_hours = {
        checkin: { ...config.operating_hours.checkin, ...(saved.operating_hours?.checkin ?? {}) },
        checkout: { ...config.operating_hours.checkout, ...(saved.operating_hours?.checkout ?? {}) },
    };
    config.timezone = timezone || config.timezone;
    config.enabled =
        config.checkin_mode !== KIOSK_CHECKIN_MODES.NOT_IN_USE ||
        config.checkout_mode !== KIOSK_CHECKOUT_MODES.NOT_IN_USE;

    return config;
}

// ---- Cache ------------------------------------------------------------

// A cached copy is only ever served back to the facility it was saved for.
// Storage can throw (private browsing, disabled storage), so every access
// degrades to "no cache" rather than breaking the kiosk.
function readCache(facilityId) {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return null;

        const cached = JSON.parse(raw);
        if (cached.facility_id !== facilityId || !cached.config) return null;

        return cached.config;
    } catch (e) {
        return null;
    }
}

function writeCache(facilityId, config) {
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify({
            facility_id: facilityId,
            saved_at: Date.now(),
            config,
        }));
    } catch (e) {
        // Nothing cached — the next page load simply fetches again.
    }
}

// Fetch both settings, throwing when they cannot be read, so loadKioskConfig
// can tell "not configured" (closed defaults) apart from "could not ask".
async function fetchKioskConfig(facilityId) {
    const [hub, tz] = await Promise.all([
        lionheart.post('/facility-setting/list', {
            facility_id: facilityId,
            name: GUEST_HUB_SETTING_NAME,
            paginate: false,
        }),
        lionheart.post('/facility-setting/list', {
            facility_id: facilityId,
            name: TIMEZONE_SETTING_NAME,
            paginate: false,
        }),
    ]);

    const timezone = tz.data.total != 0 ? tz.data.data[0].value : null;

    if (hub.data.total == 0) {
        return buildKioskConfig(null, timezone);
    }

    return buildKioskConfig(hub.data.data[0].value, timezone);
}

/**
 * Load the kiosk configuration for a facility.
 *
 * Always fetches. A page load — staff reloading the tablet after saving, or a
 * guest moving between kiosk screens — is exactly when the settings are
 * expected to be current, and a cached copy that survives a reload is
 * indistinguishable from a bug. Between loads the kiosk makes every open/close
 * decision locally against the object returned here, so nothing polls.
 *
 * The cache is a safety net, not a first choice: it answers only when the
 * request fails, so a dropped connection never closes a kiosk that is really
 * open. No saved setting leaves the closed defaults in place, so an
 * unconfigured facility shows "No Operation" rather than a flow nobody set up.
 */
export async function loadKioskConfig(facilityId) {
    if (!facilityId) return closedKioskConfig();

    try {
        const config = await fetchKioskConfig(facilityId);
        writeCache(facilityId, config);
        return config;
    } catch (e) {
        return readCache(facilityId) ?? closedKioskConfig();
    }
}
